/**
 * ScoreBadge — dark-pill score badge (247Sports / On3 recruiting-data pattern).
 *
 * Design: rectangular dark pill, tier-colored background, with the position
 * label stacked above the score number (both centered). The position label
 * (e.g. "CB") replaces the prior corner-mounted tier-name label which became
 * visually cluttered when overlapping the score number.
 *
 * Tier signal:
 *   HIGH   — green pill (Great odds)
 *   MEDIUM — amber pill (Some openings)
 *   LOW    — red pill   (Longer odds)
 *
 * Tier names are NOT rendered as visible text — the dark tier-color background
 * carries the tier signal visually, and the full tier name is preserved in
 * aria-label so screen readers still announce it. (Tradeoff vs. the original
 * FR-010 spec, which mandated visible tier text for non-color-only conveyance;
 * the visible position label takes that DOM slot now per the homepage redesign.)
 *
 * Size variants:
 *   sm — 44×30px, score 16px, label 9px  (mobile result rows)
 *   md — 56×40px, score 22px, label 9px  (desktop result rows, default)
 *   lg — 80×56px, score 30px, label 10px (hero / call-out use)
 *
 * Colors (dark pill backgrounds with colored accent text):
 *   HIGH   — bg #1A2E22, accent #5EE3A8 (green)
 *   MEDIUM — bg #2C2108, accent #F0B95E (amber)
 *   LOW    — bg #2A1010, accent #F08585 (red)
 *
 * Prop API:
 *   score    — numeric 0–100
 *   tier     — 'high' | 'medium' | 'low'
 *   position — short label rendered above the score (e.g. "CB", "LW"). Optional
 *              for backward compatibility; when omitted, only the score is shown.
 *   size     — 'sm' | 'md' | 'lg' (default: 'md')
 *   tilde    — when true, prepend a ~ to the score (fallback indicator)
 */

import React from 'react';
import { SearchResult } from '../api/search';

interface Props {
  score: number;
  tier: SearchResult['tier'];
  size?: 'sm' | 'md' | 'lg';
  /** Short position label rendered above the score (e.g. "CB", "LW"). */
  position?: string;
  /** When true, prepend a tilde to indicate a coarse-position estimate. */
  tilde?: boolean;
}

interface SizeConfig {
  width: string;
  height: string;
  scoreFontSize: string;
  labelFontSize: string;
}

const SIZE_CONFIG: Record<string, SizeConfig> = {
  sm: { width: 'w-11',  height: 'h-[30px]', scoreFontSize: 'text-base',  labelFontSize: 'text-[9px]' },
  md: { width: 'w-14',  height: 'h-10',     scoreFontSize: 'text-[22px]', labelFontSize: 'text-[9px]' },
  lg: { width: 'w-20',  height: 'h-14',     scoreFontSize: 'text-[30px]', labelFontSize: 'text-[10px]' },
};

interface TierStyle {
  bg: string;
  accent: string;
  label: string;
}

const TIER_STYLE: Record<string, TierStyle> = {
  high:   { bg: 'bg-tier-high-pill', accent: 'text-[#5EE3A8]', label: 'Great odds'    },
  medium: { bg: 'bg-tier-med-pill',  accent: 'text-[#F0B95E]', label: 'Some openings' },
  low:    { bg: 'bg-tier-low-pill',  accent: 'text-[#F08585]', label: 'Longer odds'   },
};

export function ScoreBadge({ score, tier, size = 'md', position, tilde }: Props) {
  const { width, height, scoreFontSize, labelFontSize } = SIZE_CONFIG[size] ?? SIZE_CONFIG.md;
  const { bg, accent, label } = TIER_STYLE[tier] ?? TIER_STYLE.medium;

  const roundedScore = Math.round(score);
  const ariaLabel = position
    ? `${position} opening likelihood: ${roundedScore} out of 100 — ${label}`
    : `Opening likelihood: ${roundedScore} out of 100 — ${label}`;

  return (
    <span
      className={`inline-flex flex-col items-center justify-center ${width} ${height} ${bg} rounded`}
      style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)' }}
      aria-label={ariaLabel}
    >
      {/* Position label — top, centered. Hidden from a11y tree (the aria-label on
          the parent span already includes both the position and the tier name). */}
      {position && (
        <span
          aria-hidden="true"
          className={`${labelFontSize} font-bold tracking-widest ${accent} leading-none`}
          style={{ letterSpacing: '0.1em' }}
        >
          {position}
        </span>
      )}

      {/* Score number — centered, below the position. */}
      <span
        aria-hidden="true"
        className={`${scoreFontSize} font-bold tabular-nums text-white leading-none ${position ? 'mt-1' : ''}`}
      >
        {tilde && (
          <span className={accent} aria-hidden="true">~</span>
        )}
        {roundedScore}
      </span>
    </span>
  );
}
